import { useEffect } from 'react';

const SITE_NAME = 'Nang Book';
const DEFAULT_IMAGE_PATH = '/icons/og-image.png';

export type SeoMetadata = {
  title: string;
  description: string;
  canonicalPath?: string;
  imagePath?: string;
  noIndex?: boolean;
  type?: 'website' | 'profile';
};

export function Seo({
  title,
  description,
  canonicalPath,
  imagePath = DEFAULT_IMAGE_PATH,
  noIndex = false,
  type = 'website',
}: SeoMetadata) {
  useEffect(() => {
    const canonicalUrl = toAbsoluteUrl(canonicalPath ?? window.location.pathname);
    const imageUrl = toAbsoluteUrl(imagePath);

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', noIndex ? 'noindex,nofollow' : 'index,follow');
    setMeta('name', 'application-name', SITE_NAME);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', imageUrl);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', imageUrl);
    setCanonical(canonicalUrl);
  }, [canonicalPath, description, imagePath, noIndex, title, type]);

  return null;
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.content = content;
}

function setCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }

  element.href = href;
}

function toAbsoluteUrl(pathOrUrl: string) {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const origin = getConfiguredOrigin();
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;

  return `${origin}${path}`;
}

function getConfiguredOrigin() {
  const configuredOrigin = import.meta.env.VITE_PUBLIC_SITE_URL?.trim().replace(/\/+$/, '');

  if (configuredOrigin) {
    return configuredOrigin;
  }

  return window.location.origin;
}
